import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { EmailDto, FulfilmentDto, OrderPaymentDto, PaymentGatewayDto, Settings } from '@thebb/shared';
import { del, get, post, put } from '../api';
import { useAuth } from '../auth';
import { Badge, ErrorBox, Modal, PageTitle, Spinner, fmtTime } from '../components/ui';

const kr = (minor: number, currency = 'DKK') => `${(minor / 100).toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
const captureLabel: Record<string, string> = { captured: 'Hævet', dry_run: 'Testkørsel', failed: 'Fejlet', skipped: 'Ikke hævet' };
const captureTone = (s: string) => (s === 'captured' ? 'green' : s === 'failed' ? 'red' : 'amber') as 'green' | 'red' | 'amber';
const emailLabel: Record<string, string> = { queued: 'I kø', sent: 'Sendt', failed: 'Fejlet', not_sent: 'Ikke sendt', demo: 'Demo (ikke sendt)' };
const emailTone = (s: string) => (s === 'sent' ? 'green' : s === 'failed' ? 'red' : s === 'demo' ? 'blue' : 'amber') as 'green' | 'red' | 'amber' | 'blue';

// --- order page cards -------------------------------------------------------------------------------

/** Card payment on the order page: what Bambora authorised, what we captured, capture by hand. */
export function OrderPaymentCard({ orderId }: { orderId: string }) {
  const qc = useQueryClient();
  const { can } = useAuth();
  const [error, setError] = useState<unknown>(null);
  const [amount, setAmount] = useState('');
  const p = useQuery({ queryKey: ['order-payment', orderId], queryFn: () => get<OrderPaymentDto>(`/payments/orders/${orderId}`) });
  const capture = useMutation({
    mutationFn: () => post<OrderPaymentDto>(`/payments/orders/${orderId}/capture`, amount ? { amountMinor: Math.round(Number(amount.replace(',', '.')) * 100) } : {}),
    onSuccess: (r) => { qc.setQueryData(['order-payment', orderId], r); void qc.invalidateQueries({ queryKey: ['order', orderId] }); setAmount(''); setError(null); },
    onError: (e) => { setError(e); void p.refetch(); },
  });
  const d = p.data;
  if (!d || d.provider !== 'bambora') return null;
  return (
    <div className="card space-y-2 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <div className="label m-0">Kortbetaling (Bambora)</div>
        {d.gateway ? <Badge tone={d.gateway.mode === 'live' ? 'green' : 'amber'}>{d.gateway.mode === 'live' ? 'live' : 'testkørsel'}</Badge> : <Badge tone="red">ingen konto for {d.merchantNumber ?? '?'}</Badge>}
      </div>
      <div className="grid gap-x-6 gap-y-1 sm:grid-cols-2">
        <div>Ordretotal: <b>{kr(d.totalMinor, d.currency)}</b></div>
        <div>Hævet her: <b>{kr(d.capturedMinor, d.currency)}</b></div>
        {d.remote && <div>Bambora: godkendt {kr(d.remote.authorizedMinor, d.currency)}, hævet {kr(d.remote.capturedMinor, d.currency)}</div>}
        {d.remote && <div>Til rådighed: <b>{kr(d.remote.availableMinor, d.currency)}</b>{d.remote.creditedMinor > 0 && ` · krediteret ${kr(d.remote.creditedMinor, d.currency)}`}</div>}
        {d.remoteError && <div className="text-red-700 sm:col-span-2">Bambora: {d.remoteError}</div>}
        <div className="text-muted sm:col-span-2">Forretning {d.merchantNumber ?? '-'} · transaktion {d.transactionId ?? '-'}</div>
      </div>
      <ErrorBox error={error} />
      {can('finance.invoice') && d.gateway && d.transactionId && (
        <div className="flex flex-wrap items-end gap-2">
          <div><label className="label">Beløb (tom = resten)</label><input className="input w-36" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={kr(Math.max(0, d.totalMinor - d.capturedMinor), '')} /></div>
          <button className="btn btn-primary" disabled={capture.isPending || !d.capturable} onClick={() => confirm(`Hæv ${amount ? `${amount} ${d.currency}` : 'resten'} på kortet${d.gateway?.mode === 'dry_run' ? ' (testkørsel, intet hæves)' : ''}?`) && capture.mutate()}>Hæv betaling</button>
          {!d.capturable && <span className="text-muted">Intet at hæve</span>}
        </div>
      )}
      {d.captures.length > 0 && (
        <ul className="space-y-1">
          {d.captures.map((c) => (
            <li key={c.id} className="flex flex-wrap items-center gap-2">
              <Badge tone={captureTone(c.status)}>{c.amountMinor < 0 ? 'Refunderet' : captureLabel[c.status]}</Badge>
              {c.amountMinor !== 0 && <span className="tabular-nums">{kr(Math.abs(c.amountMinor), c.currency)}</span>}
              <span className="text-muted">{c.trigger === 'ship' ? 'ved afsendelse' : 'manuelt'} · {c.actorName} · {fmtTime(c.at)}</span>
              {c.message && <span className="text-xs">{c.message}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Mails for the order, with a preview of the tracking mail for a shipped fulfilment. */
export function OrderEmails({ orderId, fulfilments }: { orderId: string; fulfilments: FulfilmentDto[] }) {
  const list = useQuery({ queryKey: ['emails', 'order', orderId], queryFn: () => get<{ items: EmailDto[] }>(`/mail/emails?orderId=${orderId}`) });
  const [open, setOpen] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ subject: string; html: string; to: string } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const shipped = fulfilments.filter((f) => f.status === 'shipped');
  const showPreview = useMutation({ mutationFn: (fid: string) => get<{ subject: string; html: string; to: string }>(`/mail/tracking-preview/${fid}`), onSuccess: setPreview, onError: setError });
  if (!list.data?.items.length && !shipped.length) return null;
  return (
    <div className="card space-y-2 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <div className="label m-0">Mails til kunden</div>
        {shipped.map((f) => <button key={f.id} className="btn ml-auto" onClick={() => showPreview.mutate(f.id)}>Vis trackingmail {shipped.length > 1 ? f.number : ''}</button>)}
      </div>
      <ErrorBox error={error} />
      {list.data?.items.map((m) => (
        <div key={m.id} className="flex flex-wrap items-center gap-2">
          <Badge tone={emailTone(m.status)}>{emailLabel[m.status]}</Badge>
          <button className="text-left text-primary hover:underline" onClick={() => setOpen(m.id)}>{m.subject}</button>
          <span className="text-muted">til {m.to} · {fmtTime(m.sentAt ?? m.createdAt)}</span>
          {m.error && <span className="text-xs text-red-700">{m.error}</span>}
        </div>
      ))}
      {list.data?.items.length === 0 && <div className="text-muted">Ingen mails sendt herfra. Trackingmails slås til under Mails.</div>}
      {open && <EmailModal id={open} onClose={() => setOpen(null)} />}
      {preview && (
        <Modal title={`Forhåndsvisning: ${preview.subject}`} onClose={() => setPreview(null)}>
          <div className="mb-2 text-sm text-muted">Til {preview.to}. Forhåndsvisningen sender ikke noget.</div>
          <iframe title="mail" className="h-[60vh] w-full rounded-lg border border-line" sandbox="" srcDoc={preview.html} />
        </Modal>
      )}
    </div>
  );
}

function EmailModal({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { can } = useAuth();
  const m = useQuery({ queryKey: ['email', id], queryFn: () => get<{ subject: string; to: string; html: string; status: string; error: string | null }>(`/mail/emails/${id}`) });
  const resend = useMutation({ mutationFn: () => post(`/mail/emails/${id}/resend`), onSuccess: () => { void qc.invalidateQueries({ queryKey: ['emails'] }); void qc.invalidateQueries({ queryKey: ['email', id] }); } });
  return (
    <Modal title={m.data?.subject ?? 'Mail'} onClose={onClose}>
      {!m.data ? <Spinner /> : (
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2"><Badge tone={emailTone(m.data.status)}>{emailLabel[m.data.status]}</Badge><span className="text-muted">til {m.data.to}</span>{can('orders.edit') && <button className="btn ml-auto" disabled={resend.isPending} onClick={() => resend.mutate()}>Send igen</button>}</div>
          {m.data.error && <div className="text-red-700">{m.data.error}</div>}
          <iframe title="mail" className="h-[60vh] w-full rounded-lg border border-line" sandbox="" srcDoc={m.data.html} />
        </div>
      )}
    </Modal>
  );
}

// --- Betaling page --------------------------------------------------------------------------------------

export function PaymentsPage() {
  const qc = useQueryClient();
  const [error, setError] = useState<unknown>(null);
  const [editing, setEditing] = useState<PaymentGatewayDto | { merchantNumber: string } | null>(null);
  const [test, setTest] = useState<Record<string, { ok: boolean; message: string }>>({});
  const data = useQuery({ queryKey: ['gateways'], queryFn: () => get<{ gateways: PaymentGatewayDto[]; missing: { merchantNumber: string; orderCount: number }[] }>('/payments/gateways') });
  const settings = useQuery({ queryKey: ['settings'], queryFn: () => get<Settings>('/settings') });
  const remove = useMutation({ mutationFn: (id: string) => del(`/payments/gateways/${id}`), onSuccess: () => void qc.invalidateQueries({ queryKey: ['gateways'] }), onError: setError });
  const runTest = useMutation({ mutationFn: (id: string) => post<{ ok: boolean; message: string }>(`/payments/gateways/${id}/test`, {}), onSuccess: (r, id) => setTest({ ...test, [id]: r }), onError: setError });
  const toggleCapture = useMutation({ mutationFn: (v: boolean) => put('/settings', { 'payments.captureOnShip': v }), onSuccess: () => void qc.invalidateQueries({ queryKey: ['settings'] }), onError: setError });
  return (
    <div className="space-y-4">
      <PageTitle title="Betaling" subtitle="Kortbetalinger fra 3x's webshops godkendes i Bambora og hæves herfra, når ordren er sendt" />
      <ErrorBox error={error} />
      <div className="card flex flex-wrap items-center gap-3 text-sm">
        <label className="flex items-center gap-2"><input type="checkbox" checked={!!settings.data?.['payments.captureOnShip']} onChange={(e) => toggleCapture.mutate(e.target.checked)} /> Hæv automatisk, når hele ordren er sendt</label>
        <span className="text-muted">Delvise forsendelser hæves ikke automatisk; hæv dem fra ordren. En konto i testkørsel noterer kun, hvad der ville være hævet.</span>
      </div>
      <table className="table card p-0">
        <thead><tr><th>Forretningsnummer</th><th>Navn</th><th>Tilstand</th><th>Nøgler</th><th className="text-right">Ordrer</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {data.data?.gateways.map((g) => (
            <tr key={g.id}>
              <td className="font-medium">{g.merchantNumber}</td>
              <td>{g.name}</td>
              <td><Badge tone={g.mode === 'live' ? 'green' : 'amber'}>{g.mode === 'live' ? 'live' : 'testkørsel'}</Badge>{g.baseUrl && <div className="text-xs text-muted">{g.baseUrl}</div>}</td>
              <td>{g.accessToken && g.secretToken ? 'udfyldt' : <span className="text-muted">mangler</span>}</td>
              <td className="text-right tabular-nums">{g.orderCount}</td>
              <td>{g.active ? <Badge tone="green">aktiv</Badge> : <Badge>slået fra</Badge>}{test[g.id] && <div className={`text-xs ${test[g.id]!.ok ? 'text-success' : 'text-red-700'}`}>{test[g.id]!.message}</div>}</td>
              <td className="whitespace-nowrap text-right">
                <button className="btn" disabled={runTest.isPending} onClick={() => runTest.mutate(g.id)}>Test</button>{' '}
                <button className="btn" onClick={() => setEditing(g)}>Redigér</button>{' '}
                <button className="btn" onClick={() => confirm(`Slet ${g.merchantNumber}?`) && remove.mutate(g.id)}>Slet</button>
              </td>
            </tr>
          ))}
          {data.data?.missing.map((m) => (
            <tr key={m.merchantNumber} className="bg-amber-50">
              <td className="font-medium">{m.merchantNumber}</td>
              <td colSpan={3} className="text-muted">Set på ordrer, men ingen konto endnu</td>
              <td className="text-right tabular-nums">{m.orderCount}</td>
              <td></td>
              <td className="text-right"><button className="btn btn-primary" onClick={() => setEditing({ merchantNumber: m.merchantNumber })}>Opret</button></td>
            </tr>
          ))}
          {data.data && data.data.gateways.length + data.data.missing.length === 0 && <tr><td colSpan={7} className="text-muted">Ingen kortbetalinger set endnu.</td></tr>}
        </tbody>
      </table>
      <button className="btn" onClick={() => setEditing({ merchantNumber: '' })}>Tilføj forretningsnummer</button>
      <div className="text-sm text-muted">Nøglerne laves i Bamboras merchant portal under Indstillinger → API-brugere: adgangsnøgle, hemmelig nøgle og forretningsnummer (fx P971699302). Brugeren skal have ret til at hæve.</div>
      {editing && <GatewayModal gateway={'id' in editing ? editing : null} merchantNumber={editing.merchantNumber} onClose={() => setEditing(null)} onError={setError} />}
    </div>
  );
}

function GatewayModal({ gateway, merchantNumber, onClose, onError }: { gateway: PaymentGatewayDto | null; merchantNumber: string; onClose: () => void; onError: (e: unknown) => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ merchantNumber: gateway?.merchantNumber ?? merchantNumber, name: gateway?.name ?? '', mode: gateway?.mode ?? 'dry_run', baseUrl: gateway?.baseUrl ?? '', accessToken: gateway?.accessToken ?? '', secretToken: gateway?.secretToken ?? '', md5Key: gateway?.md5Key ?? '', active: gateway?.active ?? true });
  const save = useMutation({
    mutationFn: () => { const body = { ...form, baseUrl: form.baseUrl || null }; return gateway ? put(`/payments/gateways/${gateway.id}`, body) : post('/payments/gateways', body); },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['gateways'] }); onClose(); },
    onError,
  });
  return (
    <Modal title={gateway ? `Bambora ${gateway.merchantNumber}` : 'Ny Bambora-konto'} onClose={onClose}>
      <div className="grid gap-3 text-sm sm:grid-cols-2">
        <div><label className="label">Forretningsnummer</label><input className="input" value={form.merchantNumber} onChange={(e) => setForm({ ...form, merchantNumber: e.target.value })} placeholder="P971699302" /></div>
        <div><label className="label">Navn</label><input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Fx Borg & Bigum webshops" /></div>
        <div><label className="label">Adgangsnøgle (access token)</label><input className="input" autoComplete="off" type="password" value={form.accessToken} onChange={(e) => setForm({ ...form, accessToken: e.target.value })} /></div>
        <div><label className="label">Hemmelig nøgle (secret token)</label><input className="input" autoComplete="off" type="password" value={form.secretToken} onChange={(e) => setForm({ ...form, secretToken: e.target.value })} /></div>
        <div><label className="label">MD5-nøgle (tjekker kortbetalinger fra webshoppen)</label><input className="input" autoComplete="off" type="password" value={form.md5Key} onChange={(e) => setForm({ ...form, md5Key: e.target.value })} /></div>
        <div><label className="label">Tilstand</label><select className="input" value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value as 'dry_run' | 'live' })}><option value="dry_run">Testkørsel (intet hæves)</option><option value="live">Live (hæver rigtige penge)</option></select></div>
        <div><label className="label">API-adresse (tom = Bambora)</label><input className="input" value={form.baseUrl} onChange={(e) => setForm({ ...form, baseUrl: e.target.value })} /></div>
        <label className="flex items-center gap-2 sm:col-span-2"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Aktiv</label>
      </div>
      <div className="mt-4 flex justify-end gap-2"><button className="btn" onClick={onClose}>Annullér</button><button className="btn btn-primary" disabled={save.isPending || !form.merchantNumber} onClick={() => save.mutate()}>Gem</button></div>
    </Modal>
  );
}

// --- Mails page -------------------------------------------------------------------------------------------

export function MailPage() {
  const { can } = useAuth();
  const [tab, setTab] = useState<'outbox' | 'setup'>('outbox');
  return (
    <div className="space-y-4">
      <PageTitle title="Mails" subtitle="Mails til kunder: track & trace, når ordren sendes" />
      <div className="flex gap-1 border-b border-line">
        {([['outbox', 'Udbakke'], ...(can('settings.manage') ? [['setup', 'Opsætning']] : [])] as [typeof tab, string][]).map(([k, l]) => (
          <button key={k} className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${tab === k ? 'border-primary text-primary' : 'border-transparent text-muted hover:text-ink'}`} onClick={() => setTab(k)}>{l}</button>
        ))}
      </div>
      {tab === 'outbox' ? <Outbox /> : <MailSetup />}
    </div>
  );
}

function Outbox() {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const params = useMemo(() => { const p = new URLSearchParams({ perPage: '100' }); if (q) p.set('q', q); if (status) p.set('status', status); return p.toString(); }, [q, status]);
  const list = useQuery({ queryKey: ['emails', params], queryFn: () => get<{ items: EmailDto[]; total: number }>(`/mail/emails?${params}`), refetchInterval: 30_000 });
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <input className="input w-60" placeholder="Søg modtager eller emne" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="input w-40" value={status} onChange={(e) => setStatus(e.target.value)}><option value="">Alle</option>{Object.entries(emailLabel).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
        <span className="self-center text-sm text-muted">{list.data?.total ?? 0} mails</span>
      </div>
      {list.isLoading ? <Spinner /> : (
        <table className="table card p-0">
          <thead><tr><th>Status</th><th>Emne</th><th>Til</th><th>Ordre</th><th>Tid</th></tr></thead>
          <tbody>
            {list.data?.items.map((m) => (
              <tr key={m.id} className="cursor-pointer hover:bg-canvas" onClick={() => setOpen(m.id)}>
                <td><Badge tone={emailTone(m.status)}>{emailLabel[m.status]}</Badge></td>
                <td>{m.subject}{m.error && <div className="text-xs text-red-700">{m.error}</div>}</td>
                <td>{m.to}</td>
                <td>{m.orderId ? <Link to="/orders/$id" params={{ id: m.orderId }} className="text-primary hover:underline" onClick={(e) => e.stopPropagation()}>{m.orderNumber}</Link> : ''}</td>
                <td className="whitespace-nowrap text-xs text-muted">{fmtTime(m.sentAt ?? m.createdAt)}</td>
              </tr>
            ))}
            {list.data?.items.length === 0 && <tr><td colSpan={5} className="text-muted">Ingen mails endnu.</td></tr>}
          </tbody>
        </table>
      )}
      {open && <EmailModal id={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

function MailSetup() {
  const qc = useQueryClient();
  const settings = useQuery({ queryKey: ['settings'], queryFn: () => get<Settings>('/settings') });
  const [form, setForm] = useState<Record<string, string | boolean>>({});
  const [testTo, setTestTo] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    const s = settings.data;
    if (s) setForm({ enabled: s['mail.enabled'], host: s['mail.smtpHost'], port: String(s['mail.smtpPort']), secure: s['mail.smtpSecure'], user: s['mail.smtpUser'], password: s['mail.smtpPassword'], from: s['mail.from'], replyTo: s['mail.replyTo'], tracking: s['mail.trackingEmail'], redirectTo: s['mail.redirectTo'] });
  }, [settings.data]);
  const save = useMutation({
    mutationFn: () => put('/settings', { 'mail.enabled': !!form.enabled, 'mail.smtpHost': String(form.host ?? ''), 'mail.smtpPort': Number(form.port) || 587, 'mail.smtpSecure': !!form.secure, 'mail.smtpUser': String(form.user ?? ''), 'mail.smtpPassword': String(form.password ?? ''), 'mail.from': String(form.from ?? ''), 'mail.replyTo': String(form.replyTo ?? ''), 'mail.trackingEmail': !!form.tracking, 'mail.redirectTo': String(form.redirectTo ?? '') }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['settings'] }); setMsg('Gemt'); setError(null); },
    onError: setError,
  });
  const test = useMutation({ mutationFn: () => post('/mail/test', { to: testTo }), onSuccess: () => { setMsg(`Testmail sendt til ${testTo}`); setError(null); }, onError: setError });
  if (!settings.data) return <Spinner />;
  const text = (k: string, label: string, props: Record<string, unknown> = {}) => <div><label className="label">{label}</label><input className="input" value={String(form[k] ?? '')} onChange={(e) => setForm({ ...form, [k]: e.target.value })} {...props} /></div>;
  const check = (k: string, label: string) => <label className="flex items-center gap-2"><input type="checkbox" checked={!!form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.checked })} /> {label}</label>;
  return (
    <div className="card max-w-2xl space-y-3 text-sm">
      <ErrorBox error={error} />
      {check('enabled', 'Send mails')}
      <div className="grid gap-3 sm:grid-cols-2">
        {text('host', 'SMTP-server', { placeholder: 'smtp.example.com' })}
        {text('port', 'Port', { type: 'number' })}
        {text('user', 'Brugernavn')}
        {text('password', 'Kode', { type: 'password', autoComplete: 'off' })}
        {text('from', 'Afsender')}
        {text('replyTo', 'Svar til (valgfri)')}
      </div>
      {check('secure', 'TLS fra start (port 465); ellers STARTTLS')}
      {check('tracking', 'Send track & trace til kunden, når ordren sendes')}
      <div className="text-muted">Hold trackingmails slået fra, så længe YouWe print/Consignor også sender dem, ellers får kunden to. Pakker der er booket i testkørsel sender aldrig mail.</div>
      {text('redirectTo', 'Send alle mails til denne adresse i stedet (test)', { placeholder: 'tom = rigtige modtagere' })}
      <div className="flex flex-wrap items-center gap-2">
        <button className="btn btn-primary" disabled={save.isPending} onClick={() => save.mutate()}>Gem</button>
        <input className="input w-60" placeholder="din@adresse.dk" value={testTo} onChange={(e) => setTestTo(e.target.value)} />
        <button className="btn" disabled={!testTo || test.isPending} onClick={() => test.mutate()}>Send testmail</button>
        {msg && <span className="text-success">{msg}</span>}
      </div>
    </div>
  );
}
